import axios, { AxiosInstance } from 'axios';
import { License, LicenseListResponse, DashboardStats, AnalyticsData } from '../types/License';
import {
  MonitoringStats,
  MonitoredSitesResponse,
  MonitoringEventsResponse,
} from '../types/Monitoring';

class DashboardAPI {
  private api: AxiosInstance;
  private readonly baseURL = require('../config/urls.js').BACKEND.DASHBOARD_API_URL;

  constructor() {
    this.api = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-store',
      },
      timeout: 10000,
    });

    // Request interceptor - Add JWT token from localStorage
    this.api.interceptors.request.use(
      (config) => {
        // Get JWT token from localStorage
        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`, config.params);
        return config;
      },
      (error) => {
        console.error('❌ API Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => {
        console.log(`✅ API Response: ${response.status} ${response.config.url}`);
        return response;
      },
      (error) => {
        console.error('❌ API Response Error:', error.response?.data || error.message);

        if (error.response?.status === 401) {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_user');
          window.location.assign('/');
        }

        return Promise.reject(error);
      }
    );
  }

  // Dashboard Statistics
  async getStats(): Promise<DashboardStats> {
    const response = await this.api.get<DashboardStats>('/stats');
    return response.data;
  }

  // License Management
  async getLicenses(params: {
    page?: number;
    limit?: number;
    status_filter?: string;
    email_filter?: string;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
  } = {}): Promise<LicenseListResponse> {
    // Map frontend params to backend params
    const backendParams = {
      page: params.page || 1,
      limit: params.limit || 20,
      status: params.status_filter || '',
      search: params.email_filter || ''
    };

    const response = await this.api.get<any>('/licenses', { params: backendParams });

    // Backend returns: { licenses, total_count, page, limit, total_pages }
    // Transform to match LicenseListResponse with PaginationInfo
    const currentPage = response.data.page || 1;
    const totalPages = response.data.total_pages || 1;

    return {
      licenses: response.data.licenses || [],
      pagination: {
        total_count: response.data.total_count || 0,
        current_page: currentPage,
        limit: response.data.limit || 20,
        total_pages: totalPages,
        has_next: currentPage < totalPages,
        has_prev: currentPage > 1
      }
    };
  }

  async getLicenseDetails(licenseId: string): Promise<License> {
    const response = await this.api.get<License>(`/license/${licenseId}`);
    return response.data;
  }

  async revokeLicense(licenseId: string): Promise<{ message: string }> {
    const response = await this.api.post(`/license/${licenseId}/revoke`);
    return response.data;
  }

  async reactivateLicense(licenseId: string): Promise<{ message: string }> {
    const response = await this.api.post(`/license/${licenseId}/reactivate`);
    return response.data;
  }

  async pauseLicense(licenseId: string): Promise<{ message: string }> {
    const response = await this.api.post(`/license/${licenseId}/pause`);
    return response.data;
  }

  async updateLicense(licenseId: string, updateData: Partial<License>): Promise<{ message: string }> {
    const response = await this.api.put(`/license/${licenseId}`, updateData);
    return response.data;
  }

  async deleteLicense(licenseId: string): Promise<{ message: string }> {
    const response = await this.api.delete(`/license/${licenseId}`);
    return response.data;
  }

  // Analytics
  async getAnalytics(): Promise<AnalyticsData> {
    const response = await this.api.get<AnalyticsData>('/analytics');
    return response.data;
  }

  // License Limits
  async getLicenseLimits(licenseId: string): Promise<{
    max_cameras: number;
    entry_cameras: number;
    exit_cameras: number;
    max_members: number;
    show_record_images?: boolean;
    enable_data_deletion?: boolean;
    enable_password_change?: boolean;
    show_dashboard_auth_tabs?: boolean;
    demography?: 'hidden' | 'gender_only' | 'both';
    updated_at?: string;
    updated_by?: string;
  }> {
    const response = await this.api.get(`/license/${licenseId}/limits`);
    return response.data;
  }

  async updateLicenseLimits(
    licenseId: string,
    limits: {
      entry_cameras: number;
      exit_cameras: number;
      max_members: number;
      platform?: string;
      extension?: string;
      show_record_images?: boolean;
      enable_data_deletion?: boolean;
      enable_password_change?: boolean;
      show_dashboard_auth_tabs?: boolean;
      demography?: 'hidden' | 'gender_only' | 'both';
    }
  ): Promise<{
    message: string;
    limits: {
      max_cameras: number;
      entry_cameras: number;
      exit_cameras: number;
      max_members: number;
      platform?: string;
      extension?: string;
      show_record_images?: boolean;
      demography?: 'hidden' | 'gender_only' | 'both';
      enable_data_deletion?: boolean;
      enable_password_change?: boolean;
      show_dashboard_auth_tabs?: boolean;
    };
  }> {
    const response = await this.api.put(`/license/${licenseId}/limits`, limits);
    return response.data;
  }

  // Login Audits
  // Audit logs (license, client auth, dashboard admin)
  async getAudits(params: {
    page?: number;
    limit?: number;
    email?: string;
    org_name?: string;
    event_type?: string;
    success?: boolean;
    search?: string;
    start_date?: string;
    end_date?: string;
  } = {}): Promise<{
    audits: Array<{
      id: string;
      email: string;
      org_name?: string;
      timestamp: string;
      client_ip: string;
      location?: string;
      event_type: string;
      login_type: string;
      success: boolean;
      failure_reason?: string;
      actor_username?: string;
      actor_role?: string;
      source?: string;
    }>;
    total: number;
    page: number;
    limit: number;
    total_pages: number;
  }> {
    const response = await this.api.get('/audits', { params });
    return response.data;
  }

  async getAuditSummary(search?: string): Promise<{
    organizations: Array<{
      org_name: string;
      license_count: number;
      total_events: number;
      latest_timestamp: string;
      licenses: Array<{
        email: string;
        event_count: number;
        logins: number;
        logouts: number;
        failures: number;
        latest_timestamp: string;
        latest_event_type: string;
      }>;
    }>;
    total_events: number;
    organization_count: number;
  }> {
    const response = await this.api.get('/audits/summary', { params: search ? { search } : {} });
    return response.data;
  }

  async getAuditStats(): Promise<{
    events_today: number;
    total_events: number;
    successful_events: number;
    failed_events: number;
    success_rate: number;
    events_by_type: Array<{ _id: string; count: number }>;
    top_locations: Array<{ _id: string; count: number }>;
    failures_by_reason: Array<{ _id: string; count: number }>;
    daily_events: Array<{ _id: string; total: number; successful: number; failed: number }>;
  }> {
    const response = await this.api.get('/audits/stats');
    return response.data;
  }

  async deleteAudit(auditId: string): Promise<{ message: string }> {
    const response = await this.api.delete(`/audits/${auditId}`);
    return response.data;
  }

  async bulkDeleteAudits(auditIds: string[]): Promise<{ message: string; deleted_count: number }> {
    const response = await this.api.post('/audits/bulk-delete', { audit_ids: auditIds });
    return response.data;
  }

  /** @deprecated use getAudits */
  async getLoginAudits(params: {
    page?: number;
    limit?: number;
    email?: string;
    success?: boolean;
    start_date?: string;
    end_date?: string;
  } = {}) {
    return this.getAudits(params);
  }

  /** @deprecated use getAuditStats */
  async getLoginAuditStats() {
    const stats = await this.getAuditStats();
    return {
      logins_today: stats.events_today,
      total_logins: stats.total_events,
      successful_logins: stats.successful_events,
      failed_logins: stats.failed_events,
      success_rate: stats.success_rate,
      logins_by_type: stats.events_by_type,
      top_locations: stats.top_locations,
      failures_by_reason: stats.failures_by_reason,
      daily_logins: stats.daily_events,
    };
  }

  /** @deprecated use deleteAudit */
  async deleteLoginAudit(auditId: string) {
    return this.deleteAudit(auditId);
  }

  /** @deprecated use bulkDeleteAudits */
  async bulkDeleteLoginAudits(auditIds: string[]) {
    return this.bulkDeleteAudits(auditIds);
  }

  // Site Monitoring (heartbeat)
  async getMonitoringStats(): Promise<MonitoringStats> {
    const response = await this.api.get('/monitoring/stats');
    return response.data;
  }

  async getMonitoredSites(params: {
    org_name?: string;
    status_filter?: string;
  } = {}): Promise<MonitoredSitesResponse> {
    const response = await this.api.get('/monitoring/sites', { params });
    return response.data;
  }

  async getMonitoringEvents(params: {
    org_name?: string;
    limit?: number;
  } = {}): Promise<MonitoringEventsResponse> {
    const response = await this.api.get('/monitoring/events', { params });
    return response.data;
  }

  // License Settings
  async getLicenseSettings(): Promise<{ success: boolean; settings: any; default_duration_days: number }> {
    const response = await this.api.get('/license-settings');
    return response.data;
  }

  async updateShowRecordImages(enabled: boolean): Promise<{ success: boolean; message: string; enabled: boolean }> {
    const response = await this.api.put('/license-settings/show-record-images', { enabled });
    return response.data;
  }

  // AI API Settings
  async getAIApiSettings(): Promise<{ success: boolean; ai_settings: any }> {
    const response = await this.api.get('/ai-api-settings');
    return response.data;
  }

  async updateAIApiSettings(aiSettings: any): Promise<{ success: boolean; message: string; ai_settings: any }> {
    const response = await this.api.put('/ai-api-settings', aiSettings);
    return response.data;
  }

  // Utility methods
  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatRelativeTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = now.getTime() - date.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    return `${Math.ceil(diffDays / 30)} months ago`;
  }

  getStatusColor(status: string): string {
    const statusColors = {
      active: 'text-green-600 bg-green-50',
      expired: 'text-red-600 bg-red-50',
      revoked: 'text-gray-600 bg-gray-50',
      paused: 'text-yellow-600 bg-yellow-50',
      deleted: 'text-gray-400 bg-gray-50'
    };
    return statusColors[status as keyof typeof statusColors] || 'text-gray-600 bg-gray-50';
  }

  getDaysRemainingColor(days: number): string {
    if (days <= 0) return 'text-red-600';
    if (days <= 7) return 'text-yellow-600';
    if (days <= 30) return 'text-blue-600';
    return 'text-green-600';
  }
}

export const dashboardApi = new DashboardAPI();
export default dashboardApi;
